import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EASE } from "../site/Motion";
import { imgUrl } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { SPECS } from "../../lib/catalog";

export const SpecSelect = ({ slug, value, onChange, testId, className = "" }) => {
  const spec = SPECS[slug];
  if (!spec) return null;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      aria-label={spec.label}
      className={`h-8 rounded-full border border-[#D4AF37]/50 bg-[#190317]/90 px-2 text-xs font-semibold text-[#F3E5AB] focus:outline-none ${className}`}
      data-testid={testId}
    >
      {spec.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
};

export const BookToggle = ({ photo, catName, slug, testId, className = "" }) => {
  const { items, add, remove, update } = useCart();
  const navigate = useNavigate();
  const inCart = items.find((i) => i.id === photo.id);
  const added = !!inCart;
  const [localSpec, setLocalSpec] = useState(SPECS[slug]?.default || "");
  const spec = inCart ? inCart.spec || localSpec : localSpec;
  const changeSpec = (v) => (inCart ? update(photo.id, { spec: v }) : setLocalSpec(v));
  const click = () => {
    if (added) {
      remove(photo.id);
      toast("Removed from your booking list");
      return;
    }
    add({ id: photo.id, title: photo.title, url: photo.url, slug, catName, spec });
    toast.success("Added to your booking list", { action: { label: "View list", onClick: () => navigate("/booking-list") } });
  };
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <SpecSelect slug={slug} value={spec} onChange={changeSpec} testId={`${testId}-spec`} />
      <button
      onClick={click}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-[transform,background-color] duration-300 hover:scale-105 ${
        added ? "bg-[#D4AF37] text-[#240620]" : "bg-[#25D366] text-[#06260f]"
      } ${className}`}
      data-testid={testId}
      aria-pressed={added}
    >
      {added ? <Check size={14} /> : <ShoppingBag size={14} />} {added ? "Added" : "Book"}
    </button>
    </div>
  );
};

export const PhotoCard = ({ photo, index, catName, slug, onOpen }) => (
  <motion.figure
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, ease: EASE, delay: (index % 4) * 0.06 }}
    className="group relative overflow-hidden rounded-[22px] border border-[#D4AF37]/20 bg-[#330B2E] transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-[#D4AF37]/60"
    data-testid={`photo-card-${index}`}
  >
    <button onClick={onOpen} className="block aspect-[4/5] w-full overflow-hidden" data-testid={`photo-open-${index}`} aria-label={`View ${photo.title}`}>
      <img src={imgUrl(photo.url)} alt={photo.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
    </button>
    {photo.festive && (
      <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold-foil px-2.5 py-1 text-[11px] font-semibold text-[#240620] shadow-lg" data-testid={`photo-festive-badge-${index}`}>
        <Sparkles size={12} /> Festive offer
      </span>
    )}
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#190317] via-[#190317]/50 to-transparent" />
    <figcaption className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-3 sm:flex-row sm:items-end sm:justify-between sm:p-4">
      <span className="line-clamp-2 text-[13px] font-medium text-[#FFF8EF] sm:text-sm">{photo.title}</span>
      <BookToggle photo={photo} catName={catName} slug={slug} testId={`photo-book-${index}`} />
    </figcaption>
  </motion.figure>
);
